import { COOKIES, HEADERS, errorEnvelopeSchema, type ErrorDetail } from "@mailflare/shared";

import { readCookie } from "./cookies";

/**
 * Base URL for the API. `VITE_API_BASE_URL` may be either the full API base
 * (e.g. `/api/v1`) or an API origin (e.g. `http://localhost:8787`); the
 * `/api/v1` suffix is appended when absent. Defaults to the same-origin proxy.
 */
function normalizeApiBase(value: string | undefined): string {
  const base = value === undefined || value.trim().length === 0 ? "/api/v1" : value.trim();
  const trimmed = base.replace(/\/+$/u, "");
  return /\/api\/v1$/u.test(trimmed) ? trimmed : `${trimmed}/api/v1`;
}

export const API_BASE_URL: string = normalizeApiBase(import.meta.env.VITE_API_BASE_URL);

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type QueryValue = string | number | boolean | null | undefined;

export type QueryParams = Readonly<Record<string, QueryValue>>;

/** Typed failure raised for any non-2xx API response. */
export class ApiError extends Error {
  override readonly name = "ApiError";
  readonly status: number;
  readonly code: string;
  readonly retryable: boolean;
  readonly requestId: string;
  readonly details: readonly ErrorDetail[] | undefined;

  constructor(init: {
    readonly status: number;
    readonly code: string;
    readonly message: string;
    readonly retryable: boolean;
    readonly requestId: string;
    readonly details?: readonly ErrorDetail[];
  }) {
    super(init.message);
    this.status = init.status;
    this.code = init.code;
    this.retryable = init.retryable;
    this.requestId = init.requestId;
    this.details = init.details;
  }

  /** True when the failure is a session/identity problem the UI can redirect on. */
  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

export interface RequestOptions {
  readonly method?: HttpMethod;
  readonly body?: unknown;
  /** Multipart body; mutually exclusive with `body`. */
  readonly formData?: FormData;
  /** Query parameters; booleans serialize to `true`/`false`, empty strings are dropped. */
  readonly query?: object;
  readonly signal?: AbortSignal;
  readonly headers?: Readonly<Record<string, string>>;
}

/** Extra CSRF token captured from a session response, used if the cookie is unreadable. */
let fallbackCsrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  fallbackCsrfToken = token;
}

function csrfToken(): string | null {
  return readCookie(COOKIES.csrf) ?? fallbackCsrfToken;
}

function buildUrl(path: string, query: object | undefined): string {
  const url = `${API_BASE_URL}${path}`;
  if (query === undefined) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (typeof value === "boolean") {
      search.set(key, value ? "true" : "false");
      continue;
    }
    if (typeof value === "number") {
      search.set(key, String(value));
      continue;
    }
    if (typeof value === "string" && value.length > 0) {
      search.set(key, value);
    }
  }
  const qs = search.toString();
  return qs.length > 0 ? `${url}?${qs}` : url;
}

function createIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Deterministic fallback for environments without WebCrypto (never shipped to
  // browsers; keeps the header present for contract tests).
  return `idem-${Date.now().toString(36)}`;
}

function buildHeaders(options: RequestOptions): Headers {
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  const method = options.method ?? "GET";

  if (options.formData === undefined && options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  if (method !== "GET") {
    const token = csrfToken();
    if (token !== null) headers.set(HEADERS.csrfToken, token);
    headers.set(HEADERS.idempotencyKey, createIdempotencyKey());
  }
  return headers;
}

function buildBody(options: RequestOptions): BodyInit | undefined {
  if (options.formData !== undefined) return options.formData;
  if (options.body !== undefined) return JSON.stringify(options.body);
  return undefined;
}

async function toApiError(response: Response): Promise<ApiError> {
  const fallback = {
    status: response.status,
    code: `HTTP_${response.status}`,
    message: response.statusText.length > 0 ? response.statusText : "Request failed",
    retryable: response.status >= 500,
    requestId: response.headers.get(HEADERS.requestId) ?? "unknown",
  };
  try {
    const parsed = errorEnvelopeSchema.safeParse(await response.json());
    if (!parsed.success) return new ApiError(fallback);
    const envelope = parsed.data.error;
    return new ApiError({
      status: response.status,
      code: envelope.code,
      message: envelope.message,
      retryable: envelope.retryable,
      requestId: envelope.request_id,
      ...(envelope.details !== undefined ? { details: envelope.details } : {}),
    });
  } catch {
    // Body was not JSON; keep the status-derived envelope.
    return new ApiError(fallback);
  }
}

async function execute(path: string, options: RequestOptions): Promise<Response> {
  const method = options.method ?? "GET";
  const response = await fetch(buildUrl(path, options.query), {
    method,
    headers: buildHeaders(options),
    credentials: "include",
    ...(buildBody(options) !== undefined ? { body: buildBody(options) as BodyInit } : {}),
    ...(options.signal !== undefined ? { signal: options.signal } : {}),
  });
  if (!response.ok) throw await toApiError(response);
  return response;
}

/** Performs a JSON request and deserializes the response body. */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await execute(path, options);
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (text.length === 0) return undefined as T;
  return JSON.parse(text) as T;
}

/** Performs a request and returns the raw text body (e.g. `.eml` downloads). */
export async function apiRequestText(path: string, options: RequestOptions = {}): Promise<string> {
  const response = await execute(path, options);
  return response.text();
}

/** Performs a request and returns a Blob (attachment content). */
export async function apiRequestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const response = await execute(path, options);
  return response.blob();
}
